import React, {useCallback, useEffect} from 'react';

import {config} from '../config';
import {
    useRegistrationContext,
    useRegistrationError,
    useRegistrationIdps,
    useRegistrationStatus,
    useRegistrationStep,
} from '../hooks/registration';
import {useNotificationContext} from '../hooks/useNotificationContext';
import {useSsoFlow} from '../hooks/useSsoFlow';
import type {SsoAbandonReason} from '../services/SsoTransport.service';
import {openRegistrationModalSignal} from '../signals/Registration.signals';
import {RegistrationError} from '../types';
import {RegistrationView} from '../views/Registration/Registration.view';

export const Registration: React.FC = () => {
    const registration = useRegistrationContext();
    const notifications = useNotificationContext();

    const step = useRegistrationStep();
    const error = useRegistrationError();
    const idps = useRegistrationIdps();

    const isOpen = step !== null;
    const isLoading = useRegistrationStatus() === 'registering';

    const setPostRegistrationStep = useCallback(() => {
        if (notifications.getPermissionState() === 'default') {
            registration.setStep('notifications');
        } else {
            registration.setStep('success');
        }
    }, [registration, notifications]);

    const onSsoMessage = useCallback(
        async (message: Parameters<typeof registration.handleSsoResult>[0]) => {
            try {
                await registration.handleSsoResult(message);
                if (registration.getStatus() === 'registered') {
                    setPostRegistrationStep();
                }
            } catch {
                registration.setError(RegistrationError.UnexpectedError);
                registration.setStep('register');
            }
        },
        [registration, setPostRegistrationStep],
    );

    const onSsoAbandoned = useCallback(
        (reason: SsoAbandonReason) => {
            if (reason === 'unavailable') {
                registration.setError(RegistrationError.UnexpectedError);
            }
            registration.setStep('register');
        },
        [registration],
    );

    const {openSso, closeSso} = useSsoFlow({onSsoMessage, onSsoAbandoned});

    useEffect(
        () =>
            openRegistrationModalSignal.add(() => {
                registration.reset();
                registration.setStep('register');
            }),
        [registration],
    );

    useEffect(() => {
        const deviceAlias = config.get('deviceAlias');
        if (!deviceAlias) {
            return;
        }
        void registration
            .register({deviceAlias, exchangeToken: true})
            .then(result => {
                if (result.type !== 'success') {
                    registration.setExpired(true);
                    registration.setStep('expired');
                }
            })
            .catch(() => {
                registration.setExpired(true);
                registration.setStep('expired');
            });
    }, [registration]);

    const handleClose = useCallback(() => {
        registration.reset();
        closeSso();
    }, [registration, closeSso]);

    const handleRegisterSubmit = useCallback(
        async (deviceAlias: string) => {
            try {
                const result = await registration.register({deviceAlias});
                switch (result.type) {
                    case 'redirect':
                        registration.setIdps([
                            {
                                redirectUrl: result.redirectUrl,
                                disablePopupFlow: result.disablePopupFlow,
                                ...result.redirectIdp,
                            },
                        ]);
                        registration.setStep('select-idp');
                        break;
                    case 'success':
                        setPostRegistrationStep();
                        break;
                }
            } catch {
                registration.setError(RegistrationError.UnexpectedError);
                registration.setStep('register');
            }
        },
        [registration, setPostRegistrationStep],
    );

    const handleReRegister = useCallback(() => {
        const deviceAlias =
            config.get('deviceAlias') || registration.getAlias();
        if (deviceAlias) {
            void handleRegisterSubmit(deviceAlias);
        }
    }, [registration, handleRegisterSubmit]);

    const handleSignOut = useCallback(async () => {
        await registration.unregister();
    }, [registration]);

    const handleIdp = useCallback(
        async (
            chosenIdp: string,
            redirectUrl?: string,
            disablePopupFlow?: boolean,
        ) => {
            if (redirectUrl) {
                openSso(redirectUrl, disablePopupFlow);
                return;
            }

            try {
                const result = await registration.register({chosenIdp});

                switch (result.type) {
                    case 'redirect':
                        openSso(result.redirectUrl, result.disablePopupFlow);
                        break;
                    case 'success':
                        setPostRegistrationStep();
                        break;
                }
            } catch {
                registration.setError(RegistrationError.UnexpectedError);
                registration.setStep('register');
            }
        },
        [openSso, registration, setPostRegistrationStep],
    );

    const handleNotificationsSubmit = useCallback(async () => {
        await notifications.requestPermission();
        registration.setStep('success');
    }, [registration, notifications]);

    const handleNotificationsSkip = useCallback(async () => {
        registration.setStep('success');
    }, [registration]);

    return (
        <RegistrationView
            error={error}
            isOpen={isOpen}
            isLoading={isLoading}
            step={step}
            idps={idps}
            handleRegisterSubmit={handleRegisterSubmit}
            handleIdp={handleIdp}
            handleNotificationsSubmit={handleNotificationsSubmit}
            handleNotificationsSkip={handleNotificationsSkip}
            handleReRegister={handleReRegister}
            handleSignOut={handleSignOut}
            handleClose={handleClose}
        />
    );
};
